// Condition DSL — the shared authoring vocabulary for custom-agent and
// notification-rule triggers. Lives in `@atbash-labs/ontology-triggers` so
// clients and agent trigger normalization reference one source of truth for
// the op / cmp / fn / window vocabulary.
//
// The proxy-server condition struct is the wire shape: `op` + `children` /
// `child` + snake_case leaves. The UI form prefers `kind` + `operands` /
// `operand`. condition-transform.ts maps between the two; this file owns the
// types + the token tables both directions share.
//
// Builder-authored leaves reference the canonical graph `:Tag.id` through
// `tag_id`; legacy records may contain a registry UUID. `tag_path` is an
// optional read-only display echo filled by the proxy on GET/eval — never
// authored on create/update.

export type ConditionWindow = '1m' | '5m' | '15m' | '1h' | '6h' | '24h'
/** Includes `last` and `delta` for proxy-server agg / deviation baselines (not all appear in the UI preset list). */
export type ConditionFn = 'avg' | 'min' | 'max' | 'last' | 'stddev' | 'delta' | 'sum' | 'count'
export type ConditionCmp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'ne'
export type DeadbandConditionCmp = 'gt' | 'gte' | 'lt' | 'lte'
/** Live-leaf comparison target. Agg / deviation leaves still require a number. */
export type ConditionScalar = number | string | boolean
export type DeviationMetric = 'zscore' | 'abs_diff' | 'pct_from_baseline'

/**
 * Optional human-authored explanation of a condition node, set by the
 * config builder (form, agent, …). When present the server returns it
 * verbatim on the corresponding ConditionEvalNode; when absent the
 * server falls back to a deterministic auto-generated description.
 *
 * Lives on every condition variant so authors can override at any
 * level — root for the trigger header, leaves for individual rows.
 */
export interface ConditionDescription {
  description?: string
}

export interface LiveConditionLeaf extends ConditionDescription {
  kind: 'live'
  /** Canonical graph `:Tag.id`; legacy records may contain a registry UUID. */
  tag_id: string
  /** Read-only resolved tag path echoed by the proxy on GET/eval — never sent on save. */
  tag_path?: string
  cmp: ConditionCmp
  /**
   * Number for numeric tags (all cmps). String for String/Text tags and
   * boolean for Boolean tags — those two only support `eq` / `ne`.
   */
  value: ConditionScalar
  /** Numeric hysteresis width; valid only for numeric values with ordered comparisons. */
  deadband?: number
}

export interface AggConditionLeaf extends ConditionDescription {
  kind: 'agg'
  /** Canonical graph `:Tag.id`; legacy records may contain a registry UUID. */
  tag_id: string
  /** Read-only resolved tag path echoed by the proxy on GET/eval — never sent on save. */
  tag_path?: string
  fn: ConditionFn
  window: ConditionWindow
  cmp: ConditionCmp
  value: number
  /** Numeric hysteresis width; valid only with ordered comparisons. */
  deadband?: number
}

export interface DeviationConditionLeaf extends ConditionDescription {
  kind: 'deviation'
  /** Canonical graph `:Tag.id`; legacy records may contain a registry UUID. */
  tag_id: string
  /** Read-only resolved tag path echoed by the proxy on GET/eval — never sent on save. */
  tag_path?: string
  /** Maps to proxy `baseline_window` on save; also used in the form as the baseline lookback. */
  window: ConditionWindow
  cmp: ConditionCmp
  value: number
  /** Numeric hysteresis width; valid only with ordered comparisons. */
  deadband?: number
  /** Proxy `baseline_fn`; defaults when serializing if omitted. */
  baseline_fn?: ConditionFn
  /** Proxy `metric`; defaults when serializing if omitted. */
  metric?: DeviationMetric
}

/**
 * Fires when a configured alarm named `alarm_name` is currently active on the
 * tag referenced by `tag_id`. Unlike the value-math leaves, this reads the
 * gateway's live active-alarm set rather than tag readings — the
 * engineer-authored alarm threshold is the ground truth, so there is no
 * `cmp`/`value`.
 */
export interface AlarmActiveConditionLeaf extends ConditionDescription {
  kind: 'alarm_active'
  /** Canonical graph `:Tag.id`; legacy records may contain a registry UUID. */
  tag_id: string
  /** Read-only resolved tag path echoed by the proxy on GET/eval — never sent on save. */
  tag_path?: string
  alarm_name: string
}

/** How an expr source is reduced. `last` with no window is the current reading. */
export type ExprSourceFn = 'avg' | 'min' | 'max' | 'last' | 'stddev' | 'delta'

/**
 * One named numeric input to an `expr` leaf. `tag_id` is the authored graph
 * `:Tag.id` or a legacy registry UUID; `tag_path` is a read-only proxy echo.
 * `fn` defaults to `last` (current reading). `window` is required unless `fn`
 * is `last`.
 */
export interface ExprConditionSource {
  tag_id: string
  tag_path?: string
  fn?: ExprSourceFn
  window?: ConditionWindow | string
}

/**
 * Numeric formula over named sources, then `cmp`/`value`. Comparisons stay
 * on the leaf — do not put them in `expression`. Aliases must match
 * {@link EXPR_ALIAS_RE} and every declared source must be used.
 */
export interface ExprConditionLeaf extends ConditionDescription {
  kind: 'expr'
  expression: string
  sources: Record<string, ExprConditionSource>
  cmp: ConditionCmp
  value: number
  deadband?: number
}

export type ConditionLeaf =
  | LiveConditionLeaf
  | AggConditionLeaf
  | DeviationConditionLeaf
  | AlarmActiveConditionLeaf
  | ExprConditionLeaf

export interface ConditionAnd extends ConditionDescription {
  kind: 'and'
  operands: Condition[]
}

export interface ConditionOr extends ConditionDescription {
  kind: 'or'
  operands: Condition[]
}

export interface ConditionNot extends ConditionDescription {
  kind: 'not'
  operand: Condition
}

export type Condition = ConditionAnd | ConditionOr | ConditionNot | ConditionLeaf

export const CONDITION_WINDOWS: ConditionWindow[] = ['1m', '5m', '15m', '1h', '6h', '24h']
export const CONDITION_FNS: ConditionFn[] = [
  'avg',
  'min',
  'max',
  'last',
  'stddev',
  'delta',
  'sum',
  'count'
]
export const CONDITION_CMPS: ConditionCmp[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'ne']
export const DEADBAND_CMPS: DeadbandConditionCmp[] = ['gt', 'gte', 'lt', 'lte']
/** Source aliases for `expr` leaves — same grammar as power-analysis bindings. */
export const EXPR_ALIAS_RE = /^[a-z][a-z0-9_]*$/
export const MAX_EXPR_SOURCES = 16
export const EXPR_SOURCE_FNS: ExprSourceFn[] = ['avg', 'min', 'max', 'last', 'stddev', 'delta']
/** String and boolean live values may only use these comparators. */
export const EQUALITY_CMPS: ConditionCmp[] = ['eq', 'ne']

export function conditionSupportsDeadband(
  condition: Condition
): condition is LiveConditionLeaf | AggConditionLeaf | DeviationConditionLeaf | ExprConditionLeaf {
  return (
    (condition.kind === 'live' ||
      condition.kind === 'agg' ||
      condition.kind === 'deviation' ||
      condition.kind === 'expr') &&
    typeof condition.value === 'number' &&
    (DEADBAND_CMPS as readonly ConditionCmp[]).includes(condition.cmp)
  )
}

export function isEqualityOnlyValue(value: ConditionScalar): boolean {
  return typeof value === 'string' || typeof value === 'boolean'
}

/**
 * Symbolic comparison spellings the LLM / UI sometimes emit, mapped to the
 * proxy DSL tokens. Centralized here so the agents normalizer and any future
 * consumer share one table.
 */
export const CMP_SYMBOL_TO_TOKEN: Record<string, ConditionCmp> = {
  '<=': 'lte',
  '>=': 'gte',
  '<': 'lt',
  '>': 'gt',
  '==': 'eq',
  '!=': 'ne'
}
